import React from "react"
import { motion } from "framer-motion"
import { Database, Workflow, GraduationCap, Cog, BarChart3, Shield } from "lucide-react"
import ProcessSection from "../components/marketing/ProcessSection"
import CTASection from "../components/marketing/CTASection"

const services = [
  {
    icon: Database,
    title: "Data Labeling & Annotation",
    description: "We label, categorize, and structure your raw data into high-quality training sets that AI models actually learn from. Whether it's images, text, or documents, we ensure precision and consistency.",
    color: "blue"
  },
  {
    icon: Workflow,
    title: "AI Workflow Integration",
    description: "We embed AI tools directly into your existing processes — no disruption, just smarter operations from day one. Your team keeps working the way they know, but faster and smarter.",
    color: "amber"
  },
  {
    icon: GraduationCap,
    title: "Specialized Domain Training",
    description: "From legal documents to medical records, we train models on your industry-specific data with expert-level precision. Your AI understands your business language.",
    color: "emerald"
  },
  {
    icon: Cog,
    title: "Custom AI Solutions",
    description: "Off-the-shelf AI doesn't fit every business. We build custom solutions tailored to your unique challenges and opportunities.",
    color: "purple"
  },
  {
    icon: BarChart3,
    title: "AI Strategy Consulting",
    description: "Not sure where to start? We assess your business, identify AI opportunities, and create a roadmap that makes sense for your budget and goals.",
    color: "cyan"
  },
  {
    icon: Shield,
    title: "Ongoing Support & Training",
    description: "AI adoption doesn't end at launch. We provide ongoing support, model updates, and team training to ensure long-term success.",
    color: "rose"
  }
]

const colorClasses = {
  blue: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400" },
  amber: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400" },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400" },
  purple: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400" },
  cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400" },
  rose: { bg: "bg-rose-500/10", border: "border-rose-500/20", text: "text-rose-400" }
}

export default function Services() {
  return (
    <div className="pt-20">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest">What We Offer</span>
            <h1 className="mt-4 text-4xl md:text-5xl font-bold text-white">Our Services</h1>
            <p className="mt-6 text-slate-400 text-lg max-w-2xl mx-auto">
              We help businesses adopt AI by preparing data, training models, and integrating AI into real workflows.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const colors = colorClasses[service.color]
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="p-6 md:p-8 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors"
                >
                  <div className={`inline-flex p-3 rounded-xl ${colors.bg} ${colors.border} border mb-4`}>
                    <service.icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{service.description}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <ProcessSection />
      <CTASection />
    </div>
  )
}